import axios from 'axios';
import { redirectToLogin } from './returnHash';

/**
 * Sets up a global axios response interceptor to handle authentication errors.
 * 
 * When a 401 (Unauthorized) or 403 (Forbidden) response is received,
 * this interceptor will automatically redirect the user to the login page
 * to re-authenticate via SSO.
 * 
 * This prevents the "ghost session" issue where users can navigate the app
 * but see no data after their session/token expires.
 */
export const setupAxiosInterceptor = () => {
  // Track if we're already redirecting to prevent multiple redirects
  let isRedirecting = false;

  axios.interceptors.response.use(
    // Pass through successful responses unchanged
    (response) => response,
    
    // Handle error responses
    (error) => {
      // Check if this is a network/connection error (server down/unreachable)
      if (!error.response) {
        // Network error - server is down, unreachable, or returned invalid response
        console.error('Network Error: Unable to connect to server', error.message);
        
        // Dispatch custom event that can be caught by error boundary or global handler
        window.dispatchEvent(new CustomEvent('server-unreachable', {
          detail: {
            message: 'Unable to connect to the server. Please check your connection or try again later.',
            error: error.message,
            code: error.code // e.g., 'ECONNREFUSED', 'ERR_NETWORK'
          }
        }));
        
        return Promise.reject(error);
      }

      // Check if this is an authentication error (401 or 403)
      const status = error?.response?.status;
      const requestUrl = error?.config?.url || '';
      
      // Check if this is a server error (500, 502, 503, 504)
      if (status >= 500 && status < 600) {
        console.error(`Server Error (${status}):`, error.response?.data);
        
        // Dispatch custom event for server errors
        window.dispatchEvent(new CustomEvent('server-error', {
          detail: {
            status,
            message: `The server encountered an error (${status}). Please try again later.`,
            url: requestUrl,
            data: error.response?.data
          }
        }));
      }
      
      // Re-authentication policy:
      //   401 (Unauthorized) = no or expired session. The 12h cookie-session lapses to a 401
      //       on *every* route, including /api/*. Always send the user through SSO to recover;
      //       otherwise an expired session silently fails every request (the "ghost session"
      //       that previously took multiple manual reloads to clear).
      //   403 (Forbidden) = authenticated but not allowed (app-level authorization denial).
      //       Re-login won't grant access, so never redirect — let the component surface it.
      // Exemptions: already redirecting, on the login-failure page, or the /auth/current_user
      // probe (App.jsx owns that redirect during initial load).
      const isLoginPage = window.location.pathname === '/login-failure';
      const isCurrentUserCheck = requestUrl.includes('/auth/current_user');

      if (status === 401 && !isRedirecting && !isLoginPage && !isCurrentUserCheck) {
        isRedirecting = true;

        console.warn(
          `Session expired or unauthenticated (401 on ${requestUrl}). Redirecting to login...`
        );

        // Redirect to Azure AD login. The auth flow sets the originalURL cookie so the
        // user returns to their current page after re-authenticating. The fragment is
        // stashed client-side first: browsers strip it from Referer, so the cookie alone
        // can never bring the user back to the right anchor.
        redirectToLogin();

        // Don't reset isRedirecting flag since we're redirecting anyway
      }
      
      // Continue rejecting the promise so components can still handle errors
      return Promise.reject(error);
    }
  );
};

/**
 * Optional: Setup a global fetch interceptor for the useFetch hook
 * This wraps the native fetch API to handle auth errors consistently
 */
export const setupFetchInterceptor = () => {
  const originalFetch = window.fetch;
  let isRedirecting = false;

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    // Mirror the axios policy: 401 (missing/expired session) always re-authenticates on
    // every route including /api/*; 403 (app-level denial) does not. See
    // setupAxiosInterceptor above for the full rationale.
    const isLoginPage = window.location.pathname === '/login-failure';
    const requestUrl = args[0] || '';
    const isCurrentUserCheck = typeof requestUrl === 'string' && requestUrl.includes('/auth/current_user');

    if (response.status === 401 && !isRedirecting && !isLoginPage && !isCurrentUserCheck) {
      isRedirecting = true;

      console.warn(
        `Session expired or unauthenticated (401 on ${requestUrl}). Redirecting to login...`
      );

      redirectToLogin();
    }

    return response;
  };
};
